import { useState, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { startOfWeek, format, startOfDay, endOfDay } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useStores, usePendingBookings } from '@/hooks';
import {
  getWorkingHoursRangeForWeek,
  getWorkingHoursRangeForDate,
  type WorkingHoursRange,
} from '@/utils/scheduleHelpers';
import { detectClientOverlapOnOtherPost, fetchBookingsInRange, mergeBookingsByUuid, getBookingPostUuid } from '@/features/bookings';
import CalendarHeader from './components/CalendarHeader/CalendarHeader';
import CalendarGrid from './components/CalendarGrid/CalendarGrid';
import type { CalendarReschedulePayload } from './components/CalendarGrid/CalendarGrid';
import ViewBookingModal from './components/ViewBookingModal/ViewBookingModal';
import CreateBookingModal from './components/CreateBookingModal/CreateBookingModal';
import EditBookingModal from './components/EditBookingModal/EditBookingModal';
import './OrdersPage.css';

const OrdersPage = observer(() => {
  const { bookingsStore, authStore, servicesStore, operatingHoursStore, postsStore, toastStore } = useStores();
  const { pendingCount, openModal } = usePendingBookings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedBooking, setSelectedBooking] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // State for edit booking modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editBookingUuid, setEditBookingUuid] = useState<string | null>(null);

  // Дата в URL используется только для шеринга ссылки в рамках текущей сессии — при
  // открытии/перезагрузке страницы календарь всегда стартует с сегодняшнего дня.
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());

  const [viewMode, setViewMode] = useState<'day' | 'week'>(() => {
    return searchParams.has('day') ? 'day' : 'week';
  });

  // State for create booking modal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createBookingDate, setCreateBookingDate] = useState<Date | null>(null);
  const [createBookingTime, setCreateBookingTime] = useState<string>('');
  const [selectedPostUuid, setSelectedPostUuid] = useState<string | null>(null);

  // Фильтр по постам для недельного вида
  const [filterPostUuids, setFilterPostUuids] = useState<string[]>([]);

  // Мемоизируем начало недели, чтобы избежать лишних ререндеров
  const weekStart = useMemo(() => {
    return startOfWeek(currentDate, { weekStartsOn: 1 });
  }, [currentDate]);

  // Рабочие часы на основе режима работы из operatingHoursStore
  const workingHours = useMemo((): WorkingHoursRange => {
    if (operatingHoursStore.regularSchedule.length === 0) {
      return { start: 9, end: 18, openMinutes: 9 * 60, closeMinutes: 18 * 60, isClosed: false };
    }

    if (viewMode === 'day') {
      return getWorkingHoursRangeForDate(
        currentDate,
        operatingHoursStore.regularSchedule,
        operatingHoursStore.specialDates,
      );
    }

    return getWorkingHoursRangeForWeek(
      weekStart,
      operatingHoursStore.regularSchedule,
      operatingHoursStore.specialDates,
    );
  }, [
    viewMode,
    currentDate,
    weekStart,
    operatingHoursStore.regularSchedule,
    operatingHoursStore.specialDates,
  ]);

  // Инициализация при первом рендере: режим (день/неделя) берём из URL, а дату — всегда
  // сегодняшнюю, независимо от того, какая дата была в ссылке (см. currentDate выше).
  useEffect(() => {
    if (isInitialized) return;

    const viewModeFromUrl: 'day' | 'week' = searchParams.has('day') ? 'day' : 'week';
    setViewMode(viewModeFromUrl);

    // Сохраняем прочие параметры (например, booking из уведомления) — переписываем
    // только day/week, а не весь URL.
    const params = new URLSearchParams(searchParams);
    if (viewModeFromUrl === 'day') {
      params.delete('week');
      params.set('day', format(new Date(), 'dd-MM-yyyy'));
    } else {
      params.delete('day');
      params.set('week', format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'dd-MM-yyyy'));
    }
    setSearchParams(params, { replace: true });

    setIsInitialized(true);
  }, [searchParams, setSearchParams, isInitialized]);

  // Синхронизируем URL с состоянием календаря при изменении даты/режима
  useEffect(() => {
    // Не обновляем URL до инициализации
    if (!isInitialized) return;

    const dayParam = searchParams.get('day');
    const weekParam = searchParams.get('week');
    const params = new URLSearchParams();

    if (viewMode === 'day') {
      const expectedDay = format(currentDate, 'dd-MM-yyyy');
      if (dayParam !== expectedDay) {
        params.set('day', expectedDay);
        setSearchParams(params, { replace: true });
      }
    } else {
      const expectedWeek = format(weekStart, 'dd-MM-yyyy');
      if (weekParam !== expectedWeek) {
        params.set('week', expectedWeek);
        setSearchParams(params, { replace: true });
      }
    }
  }, [currentDate, viewMode, weekStart, searchParams, setSearchParams, isInitialized]);

  useEffect(() => {
    // Не загружаем данные до инициализации из URL
    if (!isInitialized) return;

    // Загрузка заказов при монтировании и изменении даты или режима
    const serviceCenterUuid = authStore.user?.service_center_uuid;

    if (serviceCenterUuid) {
      // Устанавливаем UUID сервисного центра в store
      bookingsStore.setServiceCenterUuid(serviceCenterUuid);

      // Устанавливаем диапазон дат в зависимости от режима
      let dateStart: Date;
      let dateEnd: Date;

      if (viewMode === 'day') {
        // В режиме 'day' загружаем только выбранный день
        dateStart = new Date(currentDate);
        dateStart.setHours(0, 0, 0, 0);
        dateEnd = new Date(currentDate);
        dateEnd.setHours(23, 59, 59, 999);
      } else {
        // В режиме 'week' загружаем всю неделю
        const weekStartDate = startOfWeek(currentDate, { weekStartsOn: 1 });
        dateStart = weekStartDate;
        dateEnd = new Date(weekStartDate);
        dateEnd.setDate(dateEnd.getDate() + 6);
        dateEnd.setHours(23, 59, 59, 999);
      }

      bookingsStore.setDateRange(dateStart, dateEnd);

      // Загружаем данные только если их нет в кэше
      if (!bookingsStore.hasCachedData()) {
        bookingsStore.fetchBookings();
      } else {
        // Если данные есть в кэше, просто триггерим обновление через fetchBookings
        // который загрузит данные из кэша моментально
        bookingsStore.fetchBookings();
      }

      // Загружаем все категории один раз (для переключателя мойка/шиномонтаж)
      servicesStore.fetchAllCategories();

      // Загружаем сервисы если их еще нет
      if (servicesStore.services.length === 0) {
        servicesStore.fetchServices();
      }

      // Загружаем расписание работы если его еще нет
      if (operatingHoursStore.regularSchedule.length === 0) {
        operatingHoursStore.loadOperatingHours(serviceCenterUuid);
      }

      // Загружаем посты если их еще нет
      if (postsStore.postsCount === 0) {
        postsStore.fetchPosts(serviceCenterUuid);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, viewMode, authStore.user?.service_center_uuid, isInitialized]);

  // Автоматический выбор категории, если доступна только одна
  useEffect(() => {
    const available = servicesStore.availableCategories;
    if (available.length === 1 && available[0] !== servicesStore.activeCategory) {
      servicesStore.setActiveCategory(available[0]);
    }
  }, [servicesStore]);

  // Обработка query параметров для открытия бронирования
  useEffect(() => {
    const bookingParam = searchParams.get('booking');

    // Если есть параметр booking, открываем модалку
    if (bookingParam && bookingParam !== selectedBooking) {
      setSelectedBooking(bookingParam);

      // Сохраняем текущие параметры календаря при открытии модалки
      const params = new URLSearchParams(searchParams);
      params.delete('booking');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, selectedBooking, setSearchParams]);

  const handleBookingClick = (bookingUuid: string) => {
    setSelectedBooking(bookingUuid);
  };

  const handleCloseModal = () => {
    setSelectedBooking(null);
    bookingsStore.clearSelectedBooking();
    // Не перезагружаем данные при закрытии - они уже актуальны
  };

  const handleUpdateBooking = () => {
    // Данные уже обновлены в store через оптимистичное обновление
    // Ничего не делаем - store сам обновит UI
  };

  const handleEditClick = () => {
    if (selectedBooking) {
      setEditBookingUuid(selectedBooking);
      setIsEditModalOpen(true);
      setSelectedBooking(null); // Закрываем модалку просмотра
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditBookingUuid(null);
  };

  const handleEditSuccess = () => {
    // Тихо обновляем данные с сервера без показа скелетона
    bookingsStore.refreshBookings();
  };

  const handleSlotClick = (date: Date, hour: number, postUuid?: string, minutes = 0) => {
    setCreateBookingDate(date);
    const slotDate = new Date(date);
    slotDate.setHours(hour, minutes, 0, 0);
    setCreateBookingTime(format(slotDate, 'HH:mm'));
    if (postUuid) {
      setSelectedPostUuid(postUuid);
    }
    setIsCreateModalOpen(true);
  };

  const handleReschedule = async (payload: CalendarReschedulePayload): Promise<boolean> => {
    const booking = bookingsStore.getBookingFromList(payload.uuid);
    if (!booking) return false;

    if (booking.status === 'completed' || booking.status === 'cancelled') {
      toastStore.showError(
        booking.status === 'completed'
          ? 'Нельзя изменить завершённую запись'
          : 'Нельзя изменить отменённую запись',
      );
      return false;
    }

    const currentPostUuid = getBookingPostUuid(booking);
    const postChanged = Boolean(payload.post_uuid && payload.post_uuid !== currentPostUuid);
    const startChanged =
      new Date(payload.start_time).getTime() !== new Date(booking.start_time).getTime();
    const durationChanged =
      Math.abs(
        new Date(payload.end_time).getTime() -
          new Date(payload.start_time).getTime() -
          (new Date(booking.end_time).getTime() - new Date(booking.start_time).getTime()),
      ) > 1000;

    const updateData: {
      start_time: string;
      end_time?: string;
      post_uuid?: string;
    } = {
      start_time: payload.start_time,
    };

    if (durationChanged) {
      updateData.end_time = payload.end_time;
    }

    if (postChanged && payload.post_uuid) {
      updateData.post_uuid = payload.post_uuid;
    }

    if (!startChanged && !postChanged && !durationChanged) {
      return true;
    }

    const clientUuid = booking.client?.uuid ?? booking.client_uuid;
    const carUuid =
      booking.car?.uuid
      ?? (typeof booking.car_uuid === 'string' ? booking.car_uuid : undefined);
    const carLicensePlate =
      typeof booking.car?.license_plate === 'string' ? booking.car.license_plate : undefined;

    if (clientUuid) {
      const start = new Date(payload.start_time);
      let overlapBookings = [...bookingsStore.bookings, ...bookingsStore.pendingBookings];
      const serviceCenterUuid = authStore.user?.service_center_uuid;

      if (serviceCenterUuid) {
        try {
          const dayBookings = await fetchBookingsInRange({
            serviceCenterUuid,
            dateFrom: startOfDay(start),
            dateTo: endOfDay(start),
          });
          overlapBookings = mergeBookingsByUuid(dayBookings, bookingsStore.pendingBookings);
        } catch {
          toastStore.showError('Не удалось проверить пересечение записей клиента');
          return false;
        }
      }

      const overlap = detectClientOverlapOnOtherPost(
        overlapBookings,
        {
          clientUuid,
          carUuid,
          carLicensePlate,
          start,
          end: new Date(payload.end_time),
          postUuid: payload.post_uuid ?? currentPostUuid,
          excludeBookingUuid: booking.uuid,
        },
      );

      if (overlap?.kind === 'app_block') {
        toastStore.showError(
          'Клиент уже записан из приложения на это время с этим автомобилем. Выберите другую машину или время.',
        );
        return false;
      }
    }

    return bookingsStore.updateBooking(payload.uuid, updateData);
  };

  const handleAddBookingClick = () => {
    // Открываем модалку создания записи от выбранной даты (время пользователь выберет в форме)
    setCreateBookingDate(currentDate);
    setCreateBookingTime('');
    setIsCreateModalOpen(true);
  };

  const handleCreateBookingSuccess = () => {
    // Тихо обновляем данные с сервера без показа скелетона
    return bookingsStore.refreshBookings();
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setCreateBookingDate(null);
    setCreateBookingTime('');
    setSelectedPostUuid(null);
  };

  // Обработчики для постов
  const handleAddPost = async () => {
    const success = await postsStore.createPost();
    if (success) {
      toastStore.showSuccess('Пост успешно добавлен');
    } else {
      toastStore.showError('Не удалось добавить пост');
    }
  };

  const handleDeletePost = async (postUuid: string) => {
    const success = await postsStore.deletePost(postUuid);
    if (success) {
      toastStore.showSuccess('Пост успешно удален');
    } else {
      toastStore.showError('Не удалось удалить пост');
    }
  };

  const handleTogglePostAvailability = async (postUuid: string, isAvailable: boolean) => {
    const success = await postsStore.updatePostAvailability(postUuid, isAvailable);
    if (success) {
      toastStore.showSuccess(
        isAvailable
          ? 'Пост доступен для онлайн записи'
          : 'Пост скрыт от онлайн записи'
      );
    } else {
      toastStore.showError('Не удалось обновить доступность поста');
    }
  };

  // Получаем первый доступный основной сервис для загрузки слотов
  const selectedService = servicesStore.mainServices[0];
  const serviceCenterUuid = authStore.user?.service_center_uuid ?? undefined;

  // Фильтруем бронирования по активной категории, используя список услуг текущей категории.
  // (По аналогии с mobile: bookings API не отдаёт business_type в сущности заказа.)
  const visibleServiceUuids = useMemo(() => {
    return new Set(servicesStore.services.map((s) => s.uuid));
  }, [servicesStore.services]);

  const visibleBookings = useMemo(() => {
    if (visibleServiceUuids.size === 0) return [];

    // Фильтр по постам применяем только в недельном виде: сам PostFilter отображается
    // лишь там, и при переключении на день его UI пропадал, а фильтрация продолжала
    // действовать — записи невыбранных постов исчезали (при том что колонки всех постов
    // оставались), и сбросить фильтр из дневного вида было нечем.
    const isPostFilterActive = viewMode === 'week' && filterPostUuids.length > 0;

    return bookingsStore.bookings.filter((booking) => {
      const postUuid = getBookingPostUuid(booking);
      return (
        visibleServiceUuids.has(booking.service.uuid) &&
        booking.status !== 'cancelled' &&
        (!isPostFilterActive || (postUuid !== undefined && filterPostUuids.includes(postUuid)))
      );
    });
  }, [bookingsStore.bookings, visibleServiceUuids, filterPostUuids, viewMode]);

  return (
    <div className="orders-page" data-testid="orders-page">
      <div className="orders-page__container">
        <CalendarHeader
          currentDate={currentDate}
          onDateChange={setCurrentDate}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onAddBooking={handleAddBookingClick}
          pendingBookingsCount={pendingCount}
          onPendingBookingsClick={openModal}
          posts={postsStore.posts}
          selectedPostUuids={filterPostUuids}
          onSelectedPostsChange={setFilterPostUuids}
        />

        {selectedBooking && (
          <ViewBookingModal
            isOpen={selectedBooking !== null}
            onClose={handleCloseModal}
            bookingUuid={selectedBooking}
            onUpdate={handleUpdateBooking}
            onEdit={handleEditClick}
          />
        )}

        <div style={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }} data-testid="orders-calendar">
          <CalendarGrid
            bookings={visibleBookings}
            weekStart={weekStart}
            onBookingClick={handleBookingClick}
            onSlotClick={handleSlotClick}
            onReschedule={handleReschedule}
            onInvalidReschedule={(message) => toastStore.showError(message)}
            workingHours={workingHours}
            isLoading={bookingsStore.isLoading}
            serviceCenterUuid={serviceCenterUuid}
            serviceUuid={selectedService?.uuid}
            viewMode={viewMode}
            currentDate={currentDate}
            onViewModeChange={setViewMode}
            onSelectedDateChange={setCurrentDate}
            posts={postsStore.posts}
            onAddPost={handleAddPost}
            onDeletePost={handleDeletePost}
            onTogglePostAvailability={handleTogglePostAvailability}
            isPostsLoading={postsStore.isLoading}
          />
        </div>
      </div>

      {isCreateModalOpen && (
        <CreateBookingModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          onSuccess={handleCreateBookingSuccess}
          initialDate={createBookingDate || undefined}
          initialTime={createBookingTime}
          initialPostUuid={selectedPostUuid || undefined}
        />
      )}

      {isEditModalOpen && editBookingUuid && (
        <EditBookingModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          bookingUuid={editBookingUuid}
          onUpdate={handleEditSuccess}
        />
      )}
    </div>
  );
});

export default OrdersPage;
